import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AdjustmentCard } from './AdjustmentCard'
import type { AdjustmentScenario } from '@/types/api'

interface AdjustmentListProps {
  adjustments: AdjustmentScenario[]
  selectedIds: string[]
  onSelect: (id: string, selected: boolean) => void
  onEdit: (adjustment: AdjustmentScenario) => void
  onDelete: (id: string) => void
  maxSelections?: number
}

/**
 * AdjustmentList - List component for managing adjustment scenarios
 * 
 * Features:
 * - Displays all saved adjustments in a grid/list layout
 * - Empty state with CTA for creating first adjustment
 * - Selection counter showing "N selected (max M)"
 * - Validation: prevents selecting more than max (default 3)
 * - Bulk deselect option
 * - Sorting: newest first (by id timestamp)
 */
export function AdjustmentList({
  adjustments,
  selectedIds,
  onSelect,
  onEdit,
  onDelete,
  maxSelections = 3,
}: AdjustmentListProps) {
  const [showValidationError, setShowValidationError] = useState(false)

  const handleSelect = (id: string, selected: boolean) => {
    if (selected && selectedIds.length >= maxSelections) {
      setShowValidationError(true)
      // Auto-hide error after 3 seconds
      setTimeout(() => setShowValidationError(false), 3000)
      return
    }
    setShowValidationError(false)
    onSelect(id, selected)
  }

  const handleDeselectAll = () => {
    selectedIds.forEach((id) => onSelect(id, false))
  }

  // Sort adjustments by newest first (assuming ID contains timestamp)
  // Fallback to stable sort if IDs don't contain timestamps
  const sortedAdjustments = [...adjustments].sort((a, b) => {
    // Try to extract timestamps from IDs (e.g., "optimistic-1234567890")
    const timeA = parseInt(a.id.split('-')[1] || '0')
    const timeB = parseInt(b.id.split('-')[1] || '0')
    if (timeA && timeB) {
      return timeB - timeA // Descending
    }
    // Fallback: alphabetical by name
    return a.name.localeCompare(b.name)
  })

  const selectionCount = selectedIds.length
  const canSelectMore = selectionCount < maxSelections

  // Empty state
  if (adjustments.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mx-auto text-gray-300"
          >
            <path d="M12 2v20" />
            <path d="M2 12h20" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No adjustments created yet
        </h3>
        <p className="text-gray-600 max-w-md mx-auto mb-4">
          Create your first adjustment scenario to model portfolio changes for stress testing.
          You can specify position changes and compare different scenarios.
        </p>
        <p className="text-sm text-gray-500">
          Select up to {maxSelections} adjustments to include in a scenario run.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Selection Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Saved Adjustments ({adjustments.length})
          </h3>
          {selectionCount > 0 && (
            <Badge
              variant={canSelectMore ? 'default' : 'secondary'}
              className="font-medium"
            >
              {selectionCount} of {maxSelections} selected
            </Badge>
          )}
        </div>

        {selectionCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDeselectAll}
            className="text-gray-500 hover:text-gray-700"
          >
            Clear selection
          </Button>
        )}
      </div>

      {/* Validation Error */}
      {showValidationError && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
          You can only select up to {maxSelections} adjustments for a scenario run.
          Please deselect one before adding another.
        </div>
      )}

      {/* Selection Hint */}
      {selectionCount === 0 && (
        <p className="text-sm text-gray-500">
          Check the boxes to select adjustments (max {maxSelections}) for your next scenario run.
        </p>
      )}

      {/* Adjustments Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sortedAdjustments.map((adjustment) => (
          <AdjustmentCard
            key={adjustment.id}
            adjustment={adjustment}
            isSelected={selectedIds.includes(adjustment.id)}
            onSelect={handleSelect}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>

      {/* Max Selection Info */}
      {selectionCount >= maxSelections && (
        <div className="text-sm text-gray-500 text-center py-2">
          Maximum {maxSelections} adjustments selected. Deselect one to choose another.
        </div>
      )}
    </div>
  )
}

export default AdjustmentList
